import React, { useState, useEffect } from 'react';
import { X, Tag, Clock, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const PromoModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Check if user has already seen the modal
    const hasSeenPromo = localStorage.getItem('hasSeenPromoModal');
    
    if (!hasSeenPromo) {
      // Show modal after a short delay for better UX
      setTimeout(() => {
        setIsOpen(true);
      }, 1000);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('hasSeenPromoModal', 'true');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative animate-in fade-in zoom-in duration-300 my-8 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="sticky top-2 right-2 float-right text-gray-400 hover:text-gray-600 transition-colors z-10 bg-white rounded-full p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>

          {/* Content */}
          <div className="p-4 sm:p-8 text-center">
            {/* Icon/Badge */}
            <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-lg">
              <Tag className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </div>

            {/* Title */}
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] mb-2 sm:mb-3">
              Special Offer for Carriers!
            </h2>

            {/* Subtitle */}
            <div className="inline-block bg-red-50 text-red-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full font-semibold text-base sm:text-lg mb-4 sm:mb-6">
              3 Months FREE Subscription
            </div>

            {/* Description */}
            <p className="text-gray-600 mb-4 sm:mb-6 text-base sm:text-lg leading-relaxed">
              Sign up to <span className="font-bold text-[#1e3a5f]">Truckn Send It</span> and use code <span className="font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded">FREE3</span> at registration to get <strong>3 months free</strong>. Limited to the first 100 carriers — don't miss out!
            </p>

            {/* Benefits */}
            <div className="bg-gray-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 text-left">
              <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3">What you get:</p>
              <ul className="space-y-2 sm:space-y-3">
                <li className="flex items-start gap-2 sm:gap-3">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-gray-700">Unlimited access to all loads</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-gray-700">Direct shipper contact</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-gray-700">Secure payment escrow</span>
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-gray-700">
                    <strong>3 months free</strong> with code <strong>FREE3</strong>
                  </span>
                </li>
              </ul>
            </div>

            {/* Subscription Pricing */}
            <div className="bg-blue-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 border border-blue-200">
              <p className="text-xs sm:text-sm font-semibold text-[#1e3a5f] mb-3 sm:mb-4">After your free trial:</p>
              <div className="space-y-2 text-xs sm:text-sm">
                <div className="flex justify-between items-center bg-white rounded p-2 sm:p-3">
                  <span className="text-gray-700">Monthly Plan</span>
                  <span className="font-bold text-[#1e3a5f]">$49/month</span>
                </div>
                <div className="flex justify-between items-center bg-white rounded p-2 sm:p-3">
                  <span className="text-gray-700">Quarterly Plan</span>
                  <span className="font-bold text-[#1e3a5f]">$129/quarter</span>
                </div>
                <div className="flex justify-between items-center bg-white rounded p-2 sm:p-3 border-2 border-green-500">
                  <div>
                    <span className="text-gray-700">Yearly Plan</span>
                    <span className="ml-1 sm:ml-2 text-[10px] sm:text-xs bg-green-500 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">BEST VALUE</span>
                  </div>
                  <span className="font-bold text-green-600">$499/year</span>
                </div>
                <div className="flex justify-between items-center bg-white rounded p-2 sm:p-3 border border-amber-400">
                  <div>
                    <span className="text-gray-700">Ad Hoc</span>
                    <span className="ml-1 sm:ml-2 text-[10px] sm:text-xs bg-amber-500 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">PAY PER JOB</span>
                  </div>
                  <span className="font-bold text-amber-600">No subscription</span>
                </div>
              </div>
              <p className="text-[10px] sm:text-xs text-gray-600 mt-2 sm:mt-3 text-center">Cancel anytime • No hidden fees</p>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-2 sm:space-y-3">
              <Button
                onClick={() => {
                  handleClose();
                  window.location.href = '/signup?role=carrier';
                }}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-4 sm:py-6 text-base sm:text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                Start Your Free 3 Months →
              </Button>
              <button
                onClick={handleClose}
                className="w-full text-gray-500 hover:text-gray-700 text-xs sm:text-sm font-medium transition-colors py-2"
              >
                Maybe later
              </button>
            </div>

            {/* Fine print */}
            <p className="text-[10px] sm:text-xs text-gray-400 mt-4 sm:mt-6">
              Enter code FREE3 during signup. Limited to first 100 carriers.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PromoModal;
